/**
 * Selectors for transforming document state into UI-ready data.
 *
 * Pure functions that derive display data from state.
 * No side effects, easily testable.
 */

import { DocumentsState, DocumentState, PipelineStep } from './types';

/**
 * German names for categories from INDEX.yaml.
 * Used to display user-friendly names in the document feed.
 */
const CATEGORY_NAMES: Record<string, string> = {
  finance: 'Finanzen & Förderung',
  administration: 'Betriebsführung',
  livestock: 'Tierhaltung',
  fields: 'Flächen & Acker',
  machinery: 'Maschinen & Technik',
  employees: 'Mitarbeiter',
  buildings: 'Bau & Gebäude',
  partners: 'Partner',
  unsorted: 'Unsortiert',
  personal: 'Privat',
};

/**
 * Get the German display name for a category ID.
 */
export function getCategoryDisplayName(categoryId: string | undefined): string | undefined {
  if (!categoryId) return undefined;
  return CATEGORY_NAMES[categoryId] || categoryId;
}

/**
 * Get the German display name for a subcategory ID.
 * NOTE: Subcategory names now come from backend API response (loaded from INDEX.yaml).
 * This function is kept for backwards compatibility and returns the ID as a fallback
 * when German names are not available in the current context (#287).
 */
export function getSubcategoryDisplayName(subcategoryId: string | undefined): string | undefined {
  if (!subcategoryId) return undefined;
  // Subcategory names should ideally come from backend API response.
  // This function returns the ID as a reasonable fallback for display.
  // The ID is human-readable (e.g., "it-software", "accounting") and better than undefined.
  return subcategoryId;
}

// Re-export DocumentData interface for consumers
export interface DocumentData {
  doc_id: string;
  documentType: string;
  documentDate: string;
  /** When the document was uploaded to the system (ISO string) */
  uploadedAt?: string;
  description?: string;
  status: 'completed' | 'processing' | 'error';
  /** Original filename */
  filename?: string;
  /** Smart filename for download (e.g., "Rechnung_Dünger_Jan_2023.pdf") */
  smartFilename?: string;
  note?: string;
  isWaiting?: boolean;
  // Taxonomy-based classification (German display names)
  category?: string;
  subcategory?: string;
  tier?: number;
  // Icon name from feedDisplay.icon in document type YAML (lucide-react icon name)
  icon?: string;
  // Highlighted facts for folded view (from backend)
  highlightBubbles?: HighlightBubble[];
  /** Whether this document has been soft-deleted */
  isDeleted?: boolean;
  /** Tag IDs assigned to this document */
  tagIds?: string[];
}

/**
 * Step message translations.
 * Maps pipeline steps to user-friendly messages.
 * NOTE: Worker sends processing_status directly in SSE, so these are only
 * used as fallback when processing_status is not available.
 */
const STEP_MESSAGES: Record<PipelineStep, string> = {
  validate_and_convert: 'documents.steps.validate_and_convert',
  ocr_extract: 'documents.steps.ocr_extract',
  analyze_with_llm: 'documents.steps.analyze_with_llm',
  split_documents: 'documents.steps.split_documents',
  extract_facts: 'documents.steps.extract_facts',
  post_process: 'documents.steps.post_process',
  calculate_cloud_folder: 'documents.steps.calculate_cloud_folder',
  upload_to_cloud: 'documents.steps.upload_to_cloud',
  finalize: 'documents.steps.finalize',
};

/**
 * Get the i18n key for a processing step.
 */
export function getStepMessageKey(step: PipelineStep | null): string {
  if (!step) return 'documents.steps.queued';
  return STEP_MESSAGES[step] || 'documents.steps.queued';
}

/**
 * Sanitize error messages for display.
 * Converts technical errors to user-friendly messages.
 */
export function sanitizeErrorMessage(error: string | null): string {
  if (!error) return 'documents.errors.unknown';

  const lowerError = error.toLowerCase();

  if (lowerError.includes('quota') || lowerError.includes('rate limit')) {
    return 'documents.errors.quotaExceeded';
  }
  if (lowerError.includes('timeout')) {
    return 'documents.errors.timeout';
  }
  if (lowerError.includes('server') || lowerError.includes('500')) {
    return 'documents.errors.serverError';
  }

  return 'documents.errors.unknown';
}

/**
 * Get display document type.
 * Prefers the localized document role (taxonomy-v2 `role_name`), then the
 * granular display name from YAML, falling back through the raw type slug,
 * subcategory, category, filename. Never shows generic "Document".
 */
function getDisplayType(doc: DocumentState): string {
  // 1. Use the localized document role when present (#1454). Product intent:
  //    the heading IS the document role, so rows in a role sub-filter read
  //    consistently. This intentionally coarsens granular labels feed-wide
  //    (e.g. "Düngemittel-Lieferschein" -> "Lieferschein"). It also guarantees
  //    no raw English `document_type` slug ever leaks for role-carrying docs.
  if (doc.roleName) {
    return doc.roleName;
  }
  // 2. Use display name from YAML if available (e.g., "Bodenprobe / Prüfbericht")
  if (doc.documentTypeDisplayName) {
    return doc.documentTypeDisplayName;
  }
  // 3. Fall back to document type ID (but not '-other' fallback types)
  if (doc.documentType && !doc.documentType.endsWith('-other')) {
    return doc.documentType;
  }
  // 4. Use subcategory display name for tier-3/fallback types
  if (doc.subcategory) {
    const subcategoryName = getSubcategoryDisplayName(doc.subcategory);
    if (subcategoryName) {
      return subcategoryName;
    }
  }
  // 5. Use category display name
  if (doc.category) {
    const categoryName = getCategoryDisplayName(doc.category);
    if (categoryName) {
      return categoryName;
    }
  }
  // 6. For processing documents, show filename as type
  if (doc.status === 'processing' || doc.status === 'pending') {
    return doc.filename;
  }
  // 7. Final fallback - show filename for completed docs too, not generic "Document"
  return doc.filename || 'Unbekanntes Dokument';
}

/**
 * Transform a single DocumentState to DocumentData for the feed.
 */
function documentStateToFeedData(
  doc: DocumentState,
  t: (key: string) => string
): DocumentData {
  // Determine feed status
  // Check both doc.status AND processingStatus - if processingStatus starts with "Error:",
  // treat as error even if status is still 'processing' (can happen on page refresh
  // if error was written to parquet but job status wasn't updated yet)
  // Issue #920 — ``filtered`` is rendered using the existing ``error``
  // surface so the row is visually distinct from successful uploads.
  // The error_message text (e.g. "Unsupported file type: xlsx") makes
  // the cause clear in tooltips. Future PR may introduce a dedicated
  // muted/grey ``filtered`` treatment.
  let feedStatus: 'completed' | 'processing' | 'error';
  if (
    doc.status === 'failed' ||
    doc.status === 'filtered' ||
    doc.processingStatus?.startsWith('Error:')
  ) {
    feedStatus = 'error';
  } else if (doc.status === 'completed') {
    feedStatus = 'completed';
  } else {
    feedStatus = 'processing';
  }

  // Determine if waiting (no step yet - truly waiting)
  // Show spinner (not clock) when we have a step, even if stepStatus is 'queued'
  // because the backend doesn't always send 'step_started' events
  // Also don't show waiting clock if we have processingStatus (from page refresh)
  const isWaiting = doc.currentStep === null && !doc.processingStatus && feedStatus === 'processing';

  // Get note based on status
  // Priority: processingStatus from parquet (survives refresh) > derived from currentStep
  let note: string | undefined;
  if (feedStatus === 'error') {
    // For error state, use processingStatus if it contains the error message,
    // otherwise fall back to sanitized errorMessage
    if (doc.processingStatus?.startsWith('Error:')) {
      note = doc.processingStatus;
    } else {
      note = t(sanitizeErrorMessage(doc.errorMessage));
    }
  } else if (feedStatus === 'processing') {
    // Use processingStatus from parquet if available (survives page refresh)
    // Otherwise fall back to deriving from currentStep (from SSE/poll)
    if (doc.processingStatus) {
      note = doc.processingStatus;
    } else {
      note = t(getStepMessageKey(doc.currentStep));
    }
  }

  // For completed documents, include split info in note
  if (doc.status === 'completed' && doc.parentSource) {
    note = `${t('documents.splitFrom')} ${doc.parentSource.parentFilename} (${doc.parentSource.pageRangeDisplay})`;
  }

  // Use pre-computed highlighted facts from backend for folded view
  // These are computed by worker during extract_facts step with conditional rules applied
  let highlightBubbles: HighlightBubble[] | undefined = doc.highlightedFacts
    ? doc.highlightedFacts.map((hf) => ({
        name: hf.field || hf.label, // Field name for format inference, fallback to label
        label: hf.label,
        value: hf.value,
        style: hf.style || 'default',
        displayType: hf.displayType,
        scaleType: hf.scaleType,
        format: hf.format,
      }))
    : undefined;

  // Sort email highlights: date first, then sender, then subject
  if (highlightBubbles && doc.documentType === 'business-email') {
    const fieldOrder: Record<string, number> = { email_date: 0, sender: 1, subject: 2 };
    highlightBubbles = [...highlightBubbles].sort((a, b) => {
      const orderA = fieldOrder[a.name] ?? 99;
      const orderB = fieldOrder[b.name] ?? 99;
      return orderA - orderB;
    });
  }

  // For multi-document bundles, show original filename as a highlight bubble.
  // #1965 B2b: the is_multi_document marker is authoritative — the legacy
  // `documentType === 'multi-document'` sentinel dual-read was removed with the
  // classifier-v1 decommission (#2009).
  if (doc.isMultiDocument && doc.filename) {
    const filenameBubble: HighlightBubble = {
      name: 'filename',
      label: '',
      value: doc.filename,
      style: 'default',
      displayType: 'value-only',
    };
    highlightBubbles = highlightBubbles ? [filenameBubble, ...highlightBubbles] : [filenameBubble];
  }

  return {
    doc_id: doc.docId,
    documentType: getDisplayType(doc),
    // #1573: never fabricate a document date. A missing document_date must stay
    // empty so DocumentFeed routes the row to the explicit "Ohne Datum" (undated)
    // bucket (#1521) instead of silently grouping it under the upload month or a
    // non-deterministic `new Date()` "now".
    documentDate: doc.documentDate || '',
    uploadedAt: doc.uploadedAt || undefined,
    description: doc.description || undefined,
    status: feedStatus,
    filename: doc.filename,
    smartFilename: doc.smartFilename || undefined,
    note,
    isWaiting,
    // Secondary category label (#1930 B2a): the taxonomy-v2 domain's German
    // display name (resolved backend-side) wins; the legacy v1 category name
    // stays as the fallback for NULL-cell rows (trash/multi-doc/stragglers).
    category: doc.domainName || getCategoryDisplayName(doc.category || undefined),
    subcategory: getSubcategoryDisplayName(doc.subcategory || undefined),
    tier: doc.tier || undefined,
    // Icon from feedDisplay.icon in document type YAML
    icon: doc.icon || undefined,
    // Highlighted facts for folded view (from backend)
    highlightBubbles: highlightBubbles && highlightBubbles.length > 0 ? highlightBubbles : undefined,
    // User-defined tags
    tagIds: doc.tagIds && doc.tagIds.length > 0 ? doc.tagIds : undefined,
  };
}

/**
 * Sort documents for display:
 * 1. Processing/pending documents first (newest first)
 * 2. Then completed/failed by date (newest first)
 * 3. Use docId as tiebreaker for stable sorting
 */
function sortDocumentsForDisplay(docs: DocumentState[]): DocumentState[] {
  return [...docs].sort((a, b) => {
    // Processing/pending always comes first
    const aIsActive = a.status === 'processing' || a.status === 'pending';
    const bIsActive = b.status === 'processing' || b.status === 'pending';

    if (aIsActive && !bIsActive) return -1;
    if (!aIsActive && bIsActive) return 1;

    // Within same category, sort by date (newest first)
    const aDate = a.documentDate || a.uploadedAt || '';
    const bDate = b.documentDate || b.uploadedAt || '';

    const dateCompare = bDate.localeCompare(aDate);
    if (dateCompare !== 0) return dateCompare;

    // Stable tiebreaker: use docId
    return a.docId.localeCompare(b.docId);
  });
}

/**
 * Main selector: Transform entire state to feed data array.
 *
 * @param state - Current documents state
 * @param t - Translation function
 * @returns Array of DocumentData ready for DocumentFeed component
 */
export function selectFeedData(
  state: DocumentsState,
  t: (key: string) => string
): DocumentData[] {
  const documents = Array.from(state.documents.values());
  const sorted = sortDocumentsForDisplay(documents);
  return sorted.map((doc) => documentStateToFeedData(doc, t));
}

/**
 * Select processing documents (for job status polling).
 */
export function selectProcessingDocIds(state: DocumentsState): string[] {
  return Array.from(state.documents.values())
    .filter((doc) => doc.status === 'processing' || doc.status === 'pending')
    .map((doc) => doc.docId);
}

/**
 * Select documents that need step recovery (processing but no current step).
 * Used when page is refreshed mid-processing.
 * Note: We no longer require jobId since we can recover by doc_id as fallback.
 */
export function selectDocsNeedingStepRecovery(state: DocumentsState): DocumentState[] {
  return Array.from(state.documents.values()).filter(
    (doc) =>
      (doc.status === 'processing' || doc.status === 'pending') &&
      doc.currentStep === null
  );
}

/**
 * Check if any documents are currently processing.
 */
export function selectHasProcessingDocs(state: DocumentsState): boolean {
  return Array.from(state.documents.values()).some(
    (doc) => doc.status === 'processing' || doc.status === 'pending'
  );
}

/**
 * Get a specific document by ID.
 */
export function selectDocumentById(
  state: DocumentsState,
  docId: string
): DocumentState | undefined {
  return state.documents.get(docId);
}

/**
 * Get count of documents by status.
 */
export function selectStatusCounts(state: DocumentsState): Record<string, number> {
  // Issue #920 — seed every DocumentStatus value so consumers can index
  // the result by status without nullish checks when the count is zero.
  const counts: Record<string, number> = {
    pending: 0,
    processing: 0,
    completed: 0,
    failed: 0,
    filtered: 0,
  };

  for (const doc of state.documents.values()) {
    counts[doc.status] = (counts[doc.status] || 0) + 1;
  }

  return counts;
}

// ============================================
// Highlight Bubble Interface
// ============================================

/**
 * A highlight bubble to display in the document header (folded view).
 * Pre-computed by the worker and stored in parquet.
 */
export interface HighlightBubble {
  /** Field name/key */
  name: string;
  /** Human-readable label */
  label: string;
  /** Display value */
  value: string | number;
  /** Visual style */
  style: 'default' | 'alert' | 'success' | 'warning';
  /** Display type: 'text' for label: value, 'scale' for visual bar indicator, 'composite' for label value (space, no colon), 'value-only' for just the value */
  displayType?: 'text' | 'scale' | 'composite' | 'value-only';
  /** Scale type identifier (e.g., 'gehaltsklasse' for German nutrient classes A-E) */
  scaleType?: string;
  /** Value format: 'currency' for € prefix, 'sender_name' to extract name from email, 'datetime_de' for German datetime, 'date_de' for German date (DD.MM.YYYY) */
  format?: 'currency' | 'sender_name' | 'datetime_de' | 'date_de';
}
